import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Settings, Loader2 } from "lucide-react";
import type { ExportProductGroup } from "../../pages/v2/exportApi";
import { SortableImageList } from "./SortableImageList";
import { TnProductPicker } from "./TnProductPicker";
import {
  getTnProductDetails,
  updateTnProduct,
  type TnProductDetails,
} from "../../lib/tiendaNube";
import { exigirPermisoDeTienda } from "../../lib/permisosDelEquipo";

// Acción resuelta por producto: agregar/reemplazar (TN) o crear (Atlas).
export type ExportAction =
  | { kind: "add" }
  | { kind: "replace" }
  | {
      kind: "create";
      nombre: string;
      precio: string;
      descripcion: string;
      stock: string;
      sku: string;
    }
  // Enviar las fotos a OTRO producto de TN (en vez de su propia galería).
  | { kind: "send_to_other"; tnProductId: number; nombre: string };

// Campo editable: label + input/textarea (mismo estilo que el form de crear).
function EditField({
  label,
  value,
  onChange,
  textarea,
  type,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
  type?: string;
  inputMode?: "decimal" | "numeric";
}) {
  const cls =
    "w-full px-3 rounded-md border border-neutral-200 bg-surface-card text-[13px] focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors";
  return (
    <div>
      <div className="text-[11px] text-neutral-500 mb-0.5">{label}</div>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className={`${cls} py-2 resize-none`}
        />
      ) : (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          type={type}
          inputMode={inputMode}
          min={type === "number" ? 0 : undefined}
          className={`${cls} h-[var(--btn-h)]`}
        />
      )}
    </div>
  );
}

// Ítem del popup de acciones (de la tuerca).
function MenuItem({
  children,
  selected,
  onClick,
}: {
  children: React.ReactNode;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 text-[13px] text-brand-black/80 hover:bg-neutral-100 flex items-center gap-2"
    >
      {selected !== undefined && (
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${
            selected ? "bg-brand-primary" : "border border-neutral-300"
          }`}
        />
      )}
      {children}
    </button>
  );
}

// Detalle de un producto en el Paso 2:
//  - Producto de TN → muestra sus datos READ-ONLY (traídos de TN) + tuerca con
//    las acciones (Agregar/Reemplazar/Cambiar orden) en un popup.
//  - Producto de Atlas → form editable para crear el producto nuevo.
export function ProductConfigSheet({
  group,
  imagenes,
  action,
  onChangeAction,
  onReorder,
  onRemove,
  onClose,
  brandId,
}: {
  group: ExportProductGroup;
  imagenes: { id: string; orden: number; thumbUrl: string }[];
  action: ExportAction;
  onChangeAction: (a: ExportAction) => void;
  onReorder: (ids: string[]) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
  brandId: string | null;
}) {
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [editingOrder, setEditingOrder] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [details, setDetails] = useState<TnProductDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(false);
  // Form editable de los datos del producto de TN + el "original" (para el dirty).
  const emptyForm = { nombre: "", descripcion: "", precio: "", stock: "", sku: "" };
  const [form, setForm] = useState(emptyForm);
  const [orig, setOrig] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Para productos de Atlas la acción es "create"; sus campos (o vacíos).
  const cur =
    action.kind === "create"
      ? action
      : {
          kind: "create" as const,
          nombre: "",
          precio: "",
          descripcion: "",
          stock: "",
          sku: "",
        };

  // Traer datos read-only del producto de TN (auto al abrir la card).
  useEffect(() => {
    if (!group.esDeTiendanube || group.tnProductId == null) return;
    let alive = true;
    setLoadingDetails(true);
    setDetailsError(false);
    getTnProductDetails(brandId, group.tnProductId)
      .then((d) => {
        if (!alive) return;
        if (d) setDetails(d);
        else setDetailsError(true);
      })
      .finally(() => {
        if (alive) setLoadingDetails(false);
      });
    return () => {
      alive = false;
    };
  }, [group.esDeTiendanube, group.tnProductId, brandId]);

  // Al cargar los datos de TN, inicializamos el form editable + el "original".
  useEffect(() => {
    if (!details) return;
    const f = {
      nombre: details.nombre,
      descripcion: details.descripcion,
      precio: details.precio != null ? String(details.precio) : "",
      stock: details.stock != null ? String(details.stock) : "",
      sku: details.sku ?? "",
    };
    setForm(f);
    setOrig(f);
    setSaveMsg(null);
  }, [details]);

  const dirty = JSON.stringify(form) !== JSON.stringify(orig);
  const soloUnaVariante = (details?.variante_count ?? 0) <= 1;

  // "Listo" de la card: si es un producto de TN y editaste sus datos, los guarda
  // en TN antes de cerrar. Si el guardado falla, NO cierra (para no perderlos).
  const handleDone = async () => {
    if (saving) return;
    if (group.esDeTiendanube && group.tnProductId != null && dirty) {
      setSaving(true);
      setSaveMsg(null);
      // Cambiar datos en la tienda es publicar (ADR-2026-10-19-permisos-por-miembro-del-equipo).
      try {
        await exigirPermisoDeTienda();
      } catch (e) {
        setSaving(false);
        setSaveMsg(e instanceof Error ? e.message : "No tenés permiso para publicar en la tienda online.");
        return;
      }
      const ok = await updateTnProduct({
        brandId,
        tnProductId: group.tnProductId,
        varianteId: details?.variante_id ?? null,
        editarVariante: soloUnaVariante,
        nombre: form.nombre,
        descripcion: form.descripcion,
        precio: form.precio,
        stock: form.stock,
        sku: form.sku,
      });
      setSaving(false);
      if (!ok) {
        setSaveMsg("No se pudo guardar. Probá de nuevo.");
        return;
      }
      setOrig(form);
    }
    onClose();
  };

  // La tuerca tiene opciones si es de TN (Agregar/Reemplazar) o si hay >1 foto
  // (Cambiar orden). Si es Atlas con 1 foto, no hay nada → ocultamos la tuerca.
  const hasMenu = group.esDeTiendanube || imagenes.length > 1;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
        onClick={onClose}
      >
        <div
          className="relative w-full max-w-md bg-surface-card rounded-xl shadow-lg p-5 animate-modal-appear"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header: título + tuerca + cerrar */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[15px] font-semibold text-brand-black/90 truncate pr-2">
              {group.nombre}
            </h3>
            <div className="flex items-center gap-1 shrink-0">
              {hasMenu && (
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className="p-1 text-neutral-500 hover:text-neutral-800"
                  title="Acciones"
                >
                  <Settings className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-neutral-400 hover:text-neutral-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Banner: destino "otro producto" elegido */}
          {group.esDeTiendanube && action.kind === "send_to_other" && (
            <div className="mb-4 flex items-center justify-between gap-2 rounded-md border border-brand-primary/25 bg-brand-primary/5 px-3 py-2">
              <span className="text-[12.5px] text-brand-black/80">
                Se enviará a{" "}
                <span className="font-semibold">«{action.nombre}»</span>
              </span>
              <button
                type="button"
                onClick={() => onChangeAction({ kind: "add" })}
                className="text-[12px] font-medium text-brand-primary hover:opacity-70 shrink-0"
              >
                Deshacer
              </button>
            </div>
          )}

          {/* Cuerpo */}
          {editingOrder ? (
            <div>
              <div className="text-[12px] text-neutral-500 mb-1.5">
                Arrastrá para reordenar (× para quitar)
              </div>
              <SortableImageList
                imagenes={imagenes}
                onReorder={onReorder}
                onRemove={onRemove}
              />
            </div>
          ) : group.esDeTiendanube ? (
            // Datos read-only del producto de TN
            loadingDetails ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-brand-black/30" />
              </div>
            ) : detailsError ? (
              <div className="text-[13px] text-neutral-500 py-4">
                No se pudieron traer los datos de Tiendanube.
              </div>
            ) : details ? (
              <div className="space-y-2.5">
                <EditField
                  label="Nombre"
                  value={form.nombre}
                  onChange={(v) => setForm({ ...form, nombre: v })}
                />
                {soloUnaVariante ? (
                  <>
                    <EditField
                      label="Precio"
                      value={form.precio}
                      onChange={(v) => setForm({ ...form, precio: v })}
                      inputMode="decimal"
                    />
                    <EditField
                      label="Stock"
                      value={form.stock}
                      onChange={(v) => setForm({ ...form, stock: v })}
                      type="number"
                      inputMode="numeric"
                    />
                    <EditField
                      label="SKU"
                      value={form.sku}
                      onChange={(v) => setForm({ ...form, sku: v })}
                    />
                  </>
                ) : (
                  <div className="text-[12px] text-neutral-500">
                    {details.variante_count} variantes — precio/stock se editan
                    por variante (próximamente)
                  </div>
                )}
                <EditField
                  label="Descripción"
                  value={form.descripcion}
                  onChange={(v) => setForm({ ...form, descripcion: v })}
                  textarea
                />
                {saveMsg && (
                  <div className="text-[12px] text-red-600">{saveMsg}</div>
                )}
              </div>
            ) : null
          ) : (
            // Producto de Atlas: form editable para crear
            <div className="space-y-2.5">
              <div className="text-[13px] text-neutral-600">
                Se creará como producto nuevo en Tiendanube:
              </div>
              <EditField
                label="Nombre"
                value={cur.nombre}
                onChange={(v) => onChangeAction({ ...cur, nombre: v })}
              />
              <EditField
                label="Precio *"
                value={cur.precio}
                onChange={(v) => onChangeAction({ ...cur, precio: v })}
                inputMode="decimal"
              />
              <EditField
                label="Stock (opcional)"
                value={cur.stock}
                onChange={(v) => onChangeAction({ ...cur, stock: v })}
                type="number"
                inputMode="numeric"
              />
              <EditField
                label="SKU (opcional)"
                value={cur.sku}
                onChange={(v) => onChangeAction({ ...cur, sku: v })}
              />
              <EditField
                label="Descripción (opcional)"
                value={cur.descripcion}
                onChange={(v) => onChangeAction({ ...cur, descripcion: v })}
                textarea
              />
            </div>
          )}

          <button
            type="button"
            onClick={editingOrder ? () => setEditingOrder(false) : handleDone}
            disabled={saving}
            className="mt-4 w-full h-[var(--btn-h)] bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium disabled:opacity-70 transition-colors"
          >
            {saving ? "Guardando…" : editingOrder ? "Guardar" : "Listo"}
          </button>

          {/* Popup de acciones (tuerca) */}
          {menuOpen && (
            <>
              <div
                className="absolute inset-0 z-10"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute top-12 right-5 z-20 w-60 bg-surface-card rounded-lg shadow-xl border border-black/[0.08] py-1.5">
                {group.esDeTiendanube ? (
                  <>
                    <MenuItem
                      selected={action.kind === "add"}
                      onClick={() => {
                        onChangeAction({ kind: "add" });
                        setMenuOpen(false);
                      }}
                    >
                      Agregar al carrusel
                    </MenuItem>
                    <MenuItem
                      selected={action.kind === "replace"}
                      onClick={() => {
                        setMenuOpen(false);
                        setConfirmReplace(true);
                      }}
                    >
                      Reemplazar las fotos
                    </MenuItem>
                    <MenuItem
                      selected={action.kind === "send_to_other"}
                      onClick={() => {
                        setMenuOpen(false);
                        setPickerOpen(true);
                      }}
                    >
                      Agregar a otro producto
                    </MenuItem>
                    {imagenes.length > 1 && (
                      <>
                        <div className="h-px bg-black/[0.06] my-1" />
                        <MenuItem
                          onClick={() => {
                            setMenuOpen(false);
                            setEditingOrder(true);
                          }}
                        >
                          Cambiar orden
                        </MenuItem>
                      </>
                    )}
                  </>
                ) : (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      setEditingOrder(true);
                    }}
                  >
                    Cambiar orden
                  </MenuItem>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pickerOpen && (
        <TnProductPicker
          brandId={brandId}
          excludeTnProductId={group.tnProductId}
          onSelect={(p) => {
            onChangeAction({
              kind: "send_to_other",
              tnProductId: p.id,
              nombre: p.nombre,
            });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {confirmReplace && (
        <div className="fixed inset-0 z-[210] bg-black/40 flex items-center justify-center px-4">
          <div
            className="bg-surface-card rounded-lg shadow-xl w-full max-w-sm p-5 animate-modal-appear"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[16px] font-semibold text-brand-black mb-2">
              Reemplazar fotos en Tiendanube
            </h3>
            <p className="text-[14px] text-brand-black/80 leading-snug mb-4">
              Vas a <span className="font-semibold">borrar las fotos actuales</span>{" "}
              del producto en Tiendanube y dejar solo las que elegiste.
            </p>
            <div className="flex gap-2.5">
              <button
                type="button"
                onClick={() => setConfirmReplace(false)}
                className="flex-1 h-10 rounded-md text-[14px] font-medium text-neutral-600 bg-surface-card border border-neutral-200 shadow-sm hover:bg-neutral-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  onChangeAction({ kind: "replace" });
                  setConfirmReplace(false);
                }}
                className="flex-1 h-10 rounded-md text-[14px] font-medium text-white bg-red-600 hover:bg-red-700 shadow-sm transition-colors"
              >
                Sí, reemplazar
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

export default ProductConfigSheet;
